import { useEffect, useRef, useState } from 'react';
import { AreaChip } from '../components/area-chip.js';
import { api, call } from '../platform/api.js';
import { navigate } from '../platform/router.js';
import { canLinkBarcodes } from '../platform/barcodes.js';
import { scanEngine, startScan, type ScanSession } from '../platform/scan.js';
import type { CodeMatches, ScannedItem } from '../../../api/src/catalog/codes.js';
import { t, tStatus } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';

/**
 * The scanner — Phase 3 step 3's daily-ops half.
 *
 * WHAT IT IS FOR: someone is holding a garment and wants to know what it is, whether
 * it can go out, and where it lives. The whole screen is built around getting from
 * "camera open" to "that answer" in under two seconds, which is the masterplan's
 * criterion.
 *
 * THE RESULT IS SHOWN, NOT NAVIGATED TO. An early version went straight to the item
 * detail; that is wrong for a rack workflow, because the next thing is usually to
 * scan the NEXT garment, and a round trip through a detail screen makes a queue of
 * twenty items unbearable. So the answer appears over the viewfinder with the two
 * things that decide what happens next — status and open flags — and "Scan the next
 * one" is one tap. Opening the full item is the deliberate act, not the default.
 *
 * THE CAMERA IS RELEASED, ALWAYS. A live MediaStream keeps the phone's camera light
 * on, which reads as the app watching the room. It stops on unmount, on result, and
 * on error.
 */
export function ScanScreen({
  linked,
  role,
}: {
  /** Back from the barcode picker (0.1.515): show that garment, headed "Linked". */
  linked?: { code: string; itemId: string } | undefined;
  role?: string | null | undefined;
} = {}): JSX.Element {
  // Re-render when the language changes; `t` itself is a plain call (use-language.ts).
  useLanguage();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sessionRef = useRef<ScanSession | null>(null);
  const [engine] = useState(() => scanEngine());
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<ScannedItem | null>(null);
  /** THE "JUST SCANNED" LIST (redesign 5c): the last few answers, newest first, so the
   *  person at the rack can SEE that each check-out landed rather than trusting the
   *  flash of an overlay they may have looked away from. Session-local by design — it is
   *  a working memory, not a record; `movements` is the record. */
  const [recent, setRecent] = useState<ScannedItem[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  /**
   * THE LAST ANSWER THAT WAS NOT ONE GARMENT (0.1.515): zero matches ("No item has barcode X",
   * with the link actions) or several (identical pieces sharing a brand barcode — pick one). Only
   * ever set from a SUCCESSFUL read: an offline or refused lookup stays `problem`, so a scan that
   * never reached the server can never offer to link a code that may well be linked already.
   */
  const [matches, setMatches] = useState<CodeMatches | null>(null);
  /** The code behind `found`, normalised by the server — what "Link to another item" links. */
  const [foundCode, setFoundCode] = useState<string | null>(null);
  const [justLinked, setJustLinked] = useState(false);
  const mayLink = canLinkBarcodes(role);

  const stop = (): void => {
    sessionRef.current?.stop();
    sessionRef.current = null;
    setScanning(false);
  };

  // Release the camera when the screen goes away, whatever the reason.
  useEffect(() => stop, []);

  const show = (item: ScannedItem, code: string): void => {
    setFound(item);
    setFoundCode(code);
    setMatches(null);
    // Newest first, capped at eight, deduped by id so re-scanning one garment while
    // deciding does not fill the list with itself.
    setRecent((was) => [item, ...was.filter((r) => r.id !== item.id)].slice(0, 8));
  };

  const lookUp = async (code: string, pick?: string): Promise<void> => {
    setJustLinked(false);
    // `findAllByCode` since 0.1.515: one barcode may sit on several garments (the twin case),
    // and zero matches is an answer the screen acts on rather than a refusal.
    const result = await call('findAllByCode', () => api.findAllByCode.query({ code }));
    if (!result.ok) {
      setFound(null);
      setMatches(null);
      // The server's own sentence (a code too long to be one, a stale session, the network).
      setProblem(result.failure.message);
      return;
    }
    setProblem(null);
    const { items } = result.data;
    const chosen = pick === undefined ? undefined : items.find((i) => i.id === pick);
    if (chosen !== undefined) show(chosen, result.data.code);
    else if (items.length === 1 && items[0] !== undefined) show(items[0], result.data.code);
    else {
      setFound(null);
      setFoundCode(null);
      setMatches(result.data);
    }
  };

  // Back from "Link to existing item": look the code up again (the server is the record of the
  // link, not the URL) and show the garment it was linked to, headed "Linked".
  useEffect(() => {
    if (linked === undefined) return;
    void lookUp(linked.code, linked.itemId).then(() => setJustLinked(true));
    // Once, for the route that mounted us (app.tsx keys this screen on the link).
  }, []);

  const begin = async (): Promise<void> => {
    if (scanning || !videoRef.current) return;
    setProblem(null);
    setFound(null);
    setMatches(null);
    setJustLinked(false);
    setScanning(true);
    try {
      sessionRef.current = await startScan({
        video: videoRef.current,
        onCode: (code) => {
          stop();
          void lookUp(code);
        },
        onError: (error) => {
          // A single bad frame is not worth stopping for; the header in scan.ts
          // explains why this is not fatal.
           
          console.debug('scan frame error', error);
        },
      });
    } catch (error) {
      stop();
      setProblem(
        error instanceof Error
          ? `The camera could not be opened: ${error.message}`
          : 'The camera could not be opened.',
      );
    }
  };

  return (
    <section>
      {/* THE 56px HEADER BAND (redesign 7a shell): scan wore a bare floating heading — this
          gives it the same `.screen-head` band every other screen has. */}
      <div className="screen-head board-head">
        <AreaChip area="scan" />
      </div>
      {problem !== null && <p role="alert">{problem}</p>}

      {engine === 'unavailable' ? (
        // Said plainly instead of a permission prompt that leads nowhere.
        <p>{t('scan.noCamera')}</p>
      ) : !scanning ? (
        <p>
          <button type="button" className="btn-primary" onClick={() => void begin()}>
            {t('scan.start')}
          </button>{' '}
          {engine === 'fallback' && <small>{t('scan.fallbackNotice')}</small>}
        </p>
      ) : null}

      {/* THE CAMERA STAGE (frame 5e): the viewfinder with a scan-frame guide and the Stop
          control overlaid ON it — a camera-first workflow keeps its controls on the camera, not
          in a band above it. The <video> is ALWAYS in the DOM: startScan needs the element before
          the stream arrives, and creating it on demand is a race that shows a black rectangle. */}
      <div className="scan-stage">
        <video ref={videoRef} className={scanning ? 'scan-video' : 'scan-video scan-hidden'} muted />
        {scanning && (
          <>
            <span className="scan-frame" aria-hidden="true" />
            <button type="button" className="scan-stop-overlay" onClick={stop}>
              {t('scan.stop')}
            </button>
          </>
        )}
      </div>

      {found !== null && (
        /* THE ANSWER, OVER THE VIEWFINDER (frame 5e): a status-toned left edge — alert when a
           flag blocks it, info otherwise — with the two things that decide what happens next
           (status, open flags) up top, and "Scan the next one" as the one green primary because
           the next scan is the usual next act (the rack-workflow reasoning at the top of file). */
        <div className={`scan-result${found.openFlags > 0 ? ' scan-result-flagged row-edge-alert' : ' row-edge-info'}`}>
          {justLinked && (
            // role=status: the confirmation that the pick landed, read out as well as shown.
            <p role="status" className="scan-result-linked">
              ✓ Linked barcode {foundCode} to this item
            </p>
          )}
          <div className="scan-result-head">
            <h3>{found.name}</h3>
            <span className="scan-result-status">{tStatus(found.status)}</span>
          </div>
          {found.openFlags > 0 && (
            <p role="alert" className="scan-result-flag">
              ⚑ {found.openFlags} open flag{found.openFlags === 1 ? '' : 's'} — {t('scan.doNotSend')}
            </p>
          )}
          <p className="scan-result-meta">
            <small>
              {found.brand !== null ? `${found.brand} · ` : ''}
              {found.categoryPath ?? 'no category'}
              {found.locationName !== null ? ` · ${found.locationName}` : ''}
              {found.returnDueOn !== null ? ` · due back ${found.returnDueOn}` : ''}
              {found.matchedBy === 'item-id' ? ' · matched by item link' : ''}
            </small>
          </p>
          <p className="scan-result-acts">
            {engine !== 'unavailable' && (
              <button type="button" className="btn-primary" onClick={() => void begin()}>
                {t('scan.next')}
              </button>
            )}
            <button type="button" onClick={() => navigate({ name: 'item', id: found.id })}>
              {t('scan.openItem')}
            </button>
            {/* THE TWIN (maintainer ruling, 27 Sep 2026): the identical piece in your other hand
                shares this brand barcode — link it too, straight from the answer. Not for an
                item-link scan: that code is the garment's identity, not a barcode. */}
            {mayLink && foundCode !== null && found.matchedBy !== 'item-id' && (
              <button type="button" onClick={() => navigate({ name: 'items', forBarcode: foundCode })}>
                Link to another item
              </button>
            )}
          </p>
        </div>
      )}

      {matches !== null && matches.items.length === 0 && (
        /* NO GARMENT CARRIES THIS CODE — an answer, not a failure (0.1.515). Named, with the code,
           and the two ways forward for someone who may link. A person who may not is told who can. */
        <div className="scan-result row-edge-info">
          <div className="scan-result-head">
            <h3>No item has barcode {matches.code}</h3>
          </div>
          {!matches.linkable ? (
            <p className="scan-result-meta">
              <small>Linking barcodes needs database migration 090, which has not been applied yet.</small>
            </p>
          ) : mayLink ? (
            <p className="scan-result-acts">
              <button
                type="button"
                className="btn-primary"
                onClick={() => navigate({ name: 'items', forBarcode: matches.code })}
              >
                Link to existing item
              </button>
              <button type="button" onClick={() => navigate({ name: 'add', barcode: matches.code })}>
                Link to new item
              </button>
            </p>
          ) : (
            <p className="scan-result-meta">
              <small>An admin or staff member can link it to a garment.</small>
            </p>
          )}
          {engine !== 'unavailable' && (
            <p className="scan-result-acts">
              <button type="button" onClick={() => void begin()}>
                {t('scan.next')}
              </button>
            </p>
          )}
        </div>
      )}

      {matches !== null && matches.items.length > 1 && (
        /* SEVERAL GARMENTS SHARE THIS BARCODE (0.1.515) — identical pieces carry one brand EAN.
           A short list to pick from; tapping one shows its answer card. */
        <div className="scan-result row-edge-info">
          <div className="scan-result-head">
            <h3>
              {matches.items.length}
              {matches.capped ? '+' : ''} items share barcode {matches.code}
            </h3>
          </div>
          <ul className="row-list scan-matches">
            {matches.items.map((item) => (
              <li key={item.id}>
                <button type="button" className="scan-match" onClick={() => show(item, matches.code)}>
                  <span className="scan-match-name">{item.name}</span>
                  <small>
                    {tStatus(item.status)}
                    {item.brand !== null ? ` · ${item.brand}` : ''}
                    {item.locationName !== null ? ` · ${item.locationName}` : ''}
                    {item.openFlags > 0 ? ` · ⚑ ${String(item.openFlags)}` : ''}
                  </small>
                </button>
              </li>
            ))}
          </ul>
          {matches.capped && (
            <p className="scan-result-meta">
              <small>Showing the first {matches.items.length}. Search the item list for the rest.</small>
            </p>
          )}
        </div>
      )}

      {/* The manual path is not a fallback for broken cameras only — a tag can be
          creased, faded or under a sleeve, and typing eight characters beats
          fetching a laptop. The eyebrow is the quiet section label the frames use
          (`.settings-section-label` — a generic redesign primitive despite its name; a rename
          to `.section-label` is owed now that a third screen reuses it). */}
      <h3 className="settings-section-label">{t('scan.orType')}</h3>
      <p className="form-grid">
        <label>
          {t('scan.code')}
          <input
            type="text"
            className="scan-code"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manual.trim() !== '') void lookUp(manual.trim());
            }}
          />
        </label>{' '}
        <button type="button" disabled={manual.trim() === ''} onClick={() => void lookUp(manual.trim())}>
          {t('scan.find')}
        </button>
      </p>

      {recent.length > 1 && (
        <>
          {/* "Session only" says what this list is (frame 5e): working memory, not the record —
              `movements` is the record. */}
          <div className="scan-recent-head">
            <h3 className="settings-section-label">Just scanned</h3>
            <span className="scan-recent-hint">session only</span>
          </div>
          <ul className="row-list scan-recent">
            {recent.slice(1).map((r) => (
              <li key={r.id}>
                <a
                  href={`/items/${r.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate({ name: 'item', id: r.id });
                  }}
                >
                  {r.name}
                </a>{' '}
                <small>{tStatus(r.status)}</small>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
